import { getCookie } from '@alexis/helpers/cookie';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import sha256 from 'crypto-js/sha256';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { CLOUDINARY_DAILY_KEY } from '@constants/cloudinary';

import { cloudinaryImageTransformations } from '@helpers/imageTransformations';

import useGTMTracker from '@hooks/useGTMTracker';

import {
  getCurrencyState,
  getCurrentSiteState,
  getLocaleState,
  getUserState,
} from '@redux/selectors';

const useFlightSearchGTMTracking = ({
  wegoAnalyticsClientId,
  wegoAnalyticsClientSessionId,
  nearestCityByIP,
}: {
  wegoAnalyticsClientId?: string;
  wegoAnalyticsClientSessionId?: string;
  nearestCityByIP?: Place;
}) => {
  const locale = useSelector(getLocaleState);
  const currentSite = useSelector(getCurrentSiteState);
  const currency = useSelector(getCurrencyState);
  const user = useSelector(getUserState);

  const {
    flightMetaSearchError,
    isLoading,
    finalTripViewModelsToShowBasedOnSortType,
    tripViewModels,
    search,
    isRenderingFirstResults,
  } = useFlightSearchResults();

  const conditionToGTMTrack = useMemo(() => {
    return (
      !isRenderingFirstResults &&
      !!search?.id &&
      tripViewModels.length > 0 &&
      !!wegoAnalyticsClientId &&
      !!wegoAnalyticsClientSessionId &&
      !!nearestCityByIP
    );
  }, [
    isRenderingFirstResults,
    nearestCityByIP,
    search?.id,
    tripViewModels.length,
    wegoAnalyticsClientId,
    wegoAnalyticsClientSessionId,
  ]);

  const gtmPageChangedTrackingData = useMemo(() => {
    if (!conditionToGTMTrack) {
      return null;
    }

    const { id, legs, adultsCount, childrenCount, infantsCount, cabin } = search;
    const firstLeg = legs[0];

    const { legs: legViewModels, filteredFares } = tripViewModels[0];
    const departureLegViewModel = legViewModels[0];
    const returnLegViewModel = legViewModels[1];

    const pageData: FlightSearchResultGtmPageData = {
      client_type: 'WEB_APP',
      url: window.location.href,
      domain: window.location.hostname,
      locale,
      site_code: currentSite.countryCode,
      user_country_code: nearestCityByIP?.countryCode || '',
      session_id: wegoAnalyticsClientSessionId || '',
      user_id: getCookie('wego_user_id') ?? null,
      ts_code: getCookie('wego_ts_code') ?? null,
      event: 'pageChanged',
      page_name: `Flights Results|${firstLeg.departureAirport?.code ?? firstLeg.arrivalCity.code}|${
        firstLeg.arrivalAirport?.code ?? firstLeg.departureCity.code
      }|${firstLeg.departureCity.enName}, ${firstLeg.departureCity.countryEnName} to ${
        firstLeg.arrivalCity.enName
      }, ${firstLeg.arrivalCity.countryEnName}`,
      page_type: 'flights_search_results',
      product: 'flights',
      airline_codes: departureLegViewModel.airlines.map((airline) => airline?.code).join(':'),
      image_url_flights_outbound: cloudinaryImageTransformations(
        `https://assets.wego.com/image/upload/${CLOUDINARY_DAILY_KEY}/flights/airlines_square/${departureLegViewModel.airlines[0]?.code}.png`,
        'c_fit',
        'w_100',
        'h_100',
        'q_auto',
        'f_auto',
        'fl_lossy',
      ),
      rates: filteredFares[0] ? filteredFares[0]?.fare.price.amount : '',
      legs: search.legs.map((leg) => {
        return {
          departure_airport_code: leg.departureAirport ? leg.departureAirport.code : '',
          departure_city_code: leg.departureCity.code,
          departure_city_name: leg.departureCity.enName,
          departure_country_code: leg.departureCity.countryCode,
          departure_country_name: leg.departureCity.countryEnName,
          departure_continent_name: leg.departureCity.worldRegionEnName,
          departure_continent_code: leg.departureCity.worldRegionCode,

          arrival_airport_code: leg.arrivalAirport ? leg.arrivalAirport.code : '',
          arrival_city_code: leg.arrivalCity.code,
          arrival_city_name: leg.arrivalCity.enName,
          arrival_country_code: leg.arrivalCity.countryCode,
          arrival_country_name: leg.arrivalCity.countryEnName,
          arrival_continent_name: leg.arrivalCity.worldRegionEnName,
          arrival_continent_code: leg.arrivalCity.worldRegionCode,

          outbound_date: leg.outboundDate,
        };
      }),
      outbound_date: firstLeg.outboundDate,
      cabin_class: cabin,
      adults_count: adultsCount,
      children_count: childrenCount,
      infants_count: infantsCount,
      search_id: id,
      currency_code: currency.code,
      trip_type: returnLegViewModel ? 'roundtrip' : 'oneway',
      ...(!!user?.email && { e_id: sha256(user?.email).toString() }),
    };

    if (returnLegViewModel) {
      const secondLeg = legs[1];

      pageData.image_url_flights_inbound = cloudinaryImageTransformations(
        `https://assets.wego.com/image/upload/${CLOUDINARY_DAILY_KEY}/flights/airlines_square/${returnLegViewModel.airlines[0]?.code}.png`,
        'c_fit',
        'w_100',
        'h_100',
        'q_auto',
        'f_auto',
        'fl_lossy',
      );
      pageData.inbound_date = secondLeg.outboundDate;
    }

    return pageData;
  }, [
    conditionToGTMTrack,
    currency.code,
    currentSite.countryCode,
    locale,
    nearestCityByIP?.countryCode,
    search,
    tripViewModels,
    user?.email,
    wegoAnalyticsClientSessionId,
  ]);

  useGTMTracker(gtmPageChangedTrackingData);
  useGTMTracker(
    conditionToGTMTrack
      ? {
          event: 'eventTracked',
          eventAction: 'view-result',
          eventCategory: 'flights',
        }
      : null,
  );

  const noResultGTMTrackingData = useMemo(() => {
    if (
      !!flightMetaSearchError.message ||
      (!flightMetaSearchError.message &&
        !isLoading &&
        finalTripViewModelsToShowBasedOnSortType.length === 0)
    )
      return {
        event: 'eventTracked',
        eventAction: 'no-result',
        eventCategory: 'flights',
        eventLabel: '',
      };

    return null;
  }, [finalTripViewModelsToShowBasedOnSortType.length, flightMetaSearchError.message, isLoading]);

  useGTMTracker(noResultGTMTrackingData, {
    trackOnce: false,
  });
};

export default useFlightSearchGTMTracking;
